import { useCallback, useMemo } from 'react';
import { validateRecipe } from '@shared/validation';
import { Recipe } from '@shared/models/recipe.model';
import { getProductsLookup, useProductsLookup } from '../product';
import { IRecipeStoreState } from './recipe-state.model';
import { useRecipesStore } from './recipe.store';
import { useMemoDeepEqual, useSharedMemo } from '@client/hooks/use-utils.hooks';
import { getItemById } from '@shared/helpers/array.helpers';
import { pick } from '@shared/helpers/object.helpers';
import { createLocalStorageStore } from '../../helpers/local-storage.helpers';
import {
  getVisibleRecipes,
  isDeletedRecipe,
  removeDeletedProductRefsInRecipe,
} from '@shared/helpers/recipe.helpers';
import { useLookup } from '@client/hooks/use-object.hooks';

const { set: setLastSelectedRecipes, useStore } = createLocalStorageStore<
  string[]
>('last_selected_recipe', []);

const recipesSelector = (
  state: IRecipeStoreState,
): IRecipeStoreState['recipes'] => state.recipes;

const recipeLookupSelector = (
  state: IRecipeStoreState,
): IRecipeStoreState['recipesLookup'] => state.recipesLookup;

export function useRecipeState<K extends keyof IRecipeStoreState>(
  ...keys: K[]
): Pick<IRecipeStoreState, K> {
  // Create a memoed selector function to pick properties
  const selector = useMemoDeepEqual(
    () => (o: IRecipeStoreState) => (keys.length ? pick(o, ...keys) : o),
    keys,
  );
  return useRecipesStore(selector);
}

const useRecipesInStore = (): IRecipeStoreState['recipes'] =>
  useRecipesStore(recipesSelector);

/**
 * Lookup of recipes by id. Always includes soft-deleted recipes so applied
 * element/property `recipe_id` references resolve (e.g. the struck-through name
 * in the element side panel). Pickers must not source options from this lookup.
 */
export const useRecipeLookup = (): IRecipeStoreState['recipesLookup'] =>
  useLookup(useRecipes(true));

/**
 * Non-reactive recipes accessor for callbacks.
 * @param includeDeleted Include soft-deleted recipes (needed for resolution,
 * enrichment, export and copy/paste). Defaults to `false` so pickers/lists stay
 * free of deleted recipes.
 */
export const getRecipes = (
  includeDeleted = false,
): IRecipeStoreState['recipes'] => {
  const recipes = recipesSelector(useRecipesStore.getState());
  return includeDeleted ? recipes : getVisibleRecipes(recipes);
};

export const getRecipeLookup = (): IRecipeStoreState['recipesLookup'] =>
  recipeLookupSelector(useRecipesStore.getState());

export const getCreateRecipe = (): IRecipeStoreState['createRecipe'] =>
  useRecipesStore.getState().createRecipe;

export const getDeleteRecipe = (): IRecipeStoreState['deleteRecipe'] =>
  useRecipesStore.getState().deleteRecipe;

const getUpdateRecipe = (): IRecipeStoreState['updateRecipe'] =>
  useRecipesStore.getState().updateRecipe;

/**
 * Create a recipe after validating it
 * @returns
 */
export const useCreateRecipe = (): IRecipeStoreState['createRecipe'] =>
  useCallback((recipe: Recipe, ...rest): Promise<Recipe> => {
    const createRecipe = getCreateRecipe();
    const productRecord = getProductsLookup();
    return createRecipe(validateRecipe(recipe, productRecord), ...rest);
  }, []);

/**
 * Clone a recipe after validating it
 * @returns
 */
export const useCloneRecipe = (): IRecipeStoreState['cloneRecipe'] =>
  useCallback(
    (recipe: Recipe, destinationOrganization: string, overwrite: boolean) => {
      const cloneRecipe = useRecipesStore.getState().cloneRecipe;
      const productRecord = getProductsLookup();
      return cloneRecipe(
        validateRecipe(recipe, productRecord),
        destinationOrganization,
        overwrite,
      );
    },
    [],
  );

/**
 * Update a recipe after validating it
 * @returns
 */
export const useUpdateRecipe = (): IRecipeStoreState['updateRecipe'] =>
  useCallback((recipe: Recipe, ...rest): Promise<Recipe> => {
    // A soft-deleted recipe is hidden from pickers and its Save/Edit actions are
    // disabled, so reaching this with a deleted recipe means a bug upstream. The
    // server also no-ops the update (remove/update SQL require deleted_at IS NULL).
    const existing = getRecipeLookup()[recipe.id];
    if (existing && isDeletedRecipe(existing)) {
      throw new Error(
        `Cannot update soft-deleted recipe "${existing.name}" (${recipe.id})`,
      );
    }
    const updateRecipe = getUpdateRecipe();
    const productRecord = getProductsLookup();
    return updateRecipe(validateRecipe(recipe, productRecord), ...rest);
  }, []);

/**
 * Delete a recipe
 * @returns
 */
export const useDeleteRecipe = (): IRecipeStoreState['deleteRecipe'] =>
  useRecipesStore((state) => state.deleteRecipe);

/**
 * Get and set the last selected recipes in local storage
 * @returns
 */
export const useLastSelectedRecipe = (maxNumberOfStoredRecipes = 50) => {
  const recipes = useRecipes();
  const storedIds = useStore();

  const lastSelectedRecipes = useMemo(() => {
    // `recipes` already excludes soft-deleted recipes (useRecipes defaults to
    // visible-only), so the recipe selector never offers a deleted recipe.
    const rest = recipes.filter((r) => !storedIds.includes(r.id));

    const stored: Recipe[] = [];

    // preserve same order as storedIds
    for (const id of storedIds) {
      const recipe = getItemById(recipes, id, false);
      if (recipe) stored.push(recipe);
    }

    return [...stored, ...rest];
  }, [recipes, storedIds]);

  const addRecipeToLastSelected = useCallback(
    (recipeId: string) => {
      const ids = (storedIds ?? []).filter((id) => id !== recipeId);
      setLastSelectedRecipes(
        [recipeId, ...ids].slice(0, maxNumberOfStoredRecipes),
      );
    },
    [maxNumberOfStoredRecipes, storedIds],
  );

  return useMemo(
    () => ({ recipes: lastSelectedRecipes, addRecipeToLastSelected }),
    [lastSelectedRecipes, addRecipeToLastSelected],
  );
};

/**
 * All recipes without any references to deleted products.
 *
 * @param includeDeleted When `true`, soft-deleted recipes are included. Needed
 * for resolution, enrichment, export and copy/paste so applied references keep
 * working. Defaults to `false` so recipe pickers/lists never offer a deleted
 * recipe. {@link useRecipeLookup} (resolution by id) passes `true`.
 * @returns List of recipes without the deleted product refs
 */
export const useRecipes = (includeDeleted = false): Recipe[] => {
  const recipes = useRecipesInStore();
  const products = useProductsLookup();

  return useSharedMemo(
    () => {
      const scoped = includeDeleted ? recipes : getVisibleRecipes(recipes);
      return scoped.map((recipe) =>
        removeDeletedProductRefsInRecipe(products, recipe),
      );
    },
    // Distinct cache key per scope: cacheFactory stores values by key only, so
    // sharing one key across both scopes would make the two callers thrash.
    `useRecipes:${includeDeleted ? 'all' : 'visible'}`,
    [recipes, products],
  );
};
